import React from "react";
import { Icon, Container, Eyebrow, Reveal } from "./primitives.jsx";

const prefersReduced = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ── Home hero: animated "AI solving customer pain" console ───────────────── */
export function AIConsole({ radius = 16 }) {
  const reduce = prefersReduced();

  const Connector = () => (
    <svg width="46" height="22" viewBox="0 0 46 22" style={{ display: "block", flexShrink: 0, alignSelf: "center" }}>
      <line x1="3" y1="11" x2="43" y2="11" style={{ stroke: "var(--neutral-300)" }} strokeWidth="2" strokeDasharray="2 6" strokeLinecap="round" />
      {!reduce && (
        <circle r="3.2" cy="11" style={{ fill: "var(--brand-red)" }}>
          <animate attributeName="cx" values="3;43" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.88;1" dur="1.5s" repeatCount="indefinite" />
        </circle>
      )}
    </svg>
  );

  const FlowCard = ({ icon, tone, label, sub }) => (
    <div style={{ flex: 1, minWidth: 0, background: tone === "red" ? "var(--red-50)" : "var(--neutral-50)", border: tone === "red" ? "1px solid var(--border-brand)" : "1px solid var(--border-subtle)", borderRadius: Math.max(8, radius - 4), padding: "14px 10px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7, textAlign: "center", alignSelf: "stretch" }}>
      <span style={{ width: 38, height: 38, borderRadius: 10, background: tone === "red" ? "var(--brand-red)" : tone === "green" ? "var(--color-success)" : "var(--neutral-200)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={20} strokeWidth={2} color="#fff" />
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--brand-ink)", lineHeight: 1.2, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ fontSize: 10.5, color: "var(--text-muted)", lineHeight: 1.25, whiteSpace: "nowrap" }}>{sub}</span>
    </div>
  );

  const solved = [
    { label: "Manual dispatch", tag: "Automated", done: true },
    { label: "Slow onboarding", tag: "Solving…", done: false }
  ];

  return (
    <div style={{ background: "var(--surface-card)", borderRadius: radius + 6, boxShadow: "var(--shadow-lg)", border: "1px solid var(--border-subtle)", overflow: "hidden", fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)", background: "var(--neutral-25)" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => <span key={i} style={{ width: 10, height: 10, borderRadius: 999, background: "var(--neutral-300)" }} />)}
        </div>
        <div style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>rework · ai engine</div>
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "var(--brand-red)" }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: "var(--brand-red)", animation: reduce ? "none" : "rwPulse 2s infinite" }} />
          LIVE
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", borderBottom: "1px solid var(--border-subtle)" }}>
        {[
          { k: "Resolved", v: "128", sub: "pain points" },
          { k: "Solving", v: "6", sub: "now" },
          { k: "AI uptime", v: "99.9%", sub: "" }
        ].map((m, i) => (
          <div key={i} style={{ padding: "14px 16px", borderRight: i < 2 ? "1px solid var(--border-subtle)" : "none" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{m.k}</div>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--brand-ink)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{m.v}{m.sub && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-subtle)", marginLeft: 4 }}>{m.sub}</span>}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: "20px 16px 8px", display: "flex", alignItems: "stretch", gap: 4 }}>
        <FlowCard icon="circle-alert" tone="plain" label="Pain point" sub="Customer friction" />
        <Connector />
        <svg width="104" height="104" viewBox="0 0 96 96" style={{ flexShrink: 0, alignSelf: "center", overflow: "visible" }}>
          <defs>
            <linearGradient id="rwAIChip" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FF3134" />
              <stop offset="100%" stopColor="#CE0003" />
            </linearGradient>
            <radialGradient id="rwAIRing" cx="50%" cy="50%" r="50%">
              <stop offset="50%" stopColor="#EE0003" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#EE0003" stopOpacity="0.12" />
            </radialGradient>
          </defs>
          {!reduce && [0, 1].map((i) => (
            <circle key={i} cx="48" cy="48" r="32" fill="none" stroke="url(#rwAIRing)" strokeWidth="2">
              <animate attributeName="r" values="32;50" dur="2.4s" begin={`${i * 1.2}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.7;0.6;0" keyTimes="0;0.72;1" dur="2.4s" begin={`${i * 1.2}s`} repeatCount="indefinite" />
            </circle>
          ))}
          <g className={reduce ? "" : "rw-ai-chip"} style={{ filter: "drop-shadow(0 6px 14px rgba(238,0,3,0.32))" }}>
            <g stroke="var(--brand-red)" strokeWidth="3.5" strokeLinecap="round">
              {[-16, 0, 16].map((o) => <line key={"t" + o} x1={48 + o} y1="16" x2={48 + o} y2="7" />)}
              {[-16, 0, 16].map((o) => <line key={"b" + o} x1={48 + o} y1="80" x2={48 + o} y2="89" />)}
              {[-16, 0, 16].map((o) => <line key={"l" + o} x1="16" y1={48 + o} x2="7" y2={48 + o} />)}
              {[-16, 0, 16].map((o) => <line key={"r" + o} x1="80" y1={48 + o} x2="89" y2={48 + o} />)}
            </g>
            <rect x="14" y="14" width="68" height="68" rx="18" fill="url(#rwAIChip)" />
            <rect x="17.5" y="17.5" width="61" height="61" rx="15" fill="none" stroke="#fff" strokeOpacity="0.18" strokeWidth="1.5" />
            <path d="M48 27 L50.5 41 L64 44 L50.5 47 L48 61 L45.5 47 L32 44 L45.5 41 Z" fill="#fff" />
            <text x="48" y="72" textAnchor="middle" style={{ fill: "#fff", fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 18, letterSpacing: "0.04em" }}>AI</text>
          </g>
        </svg>
        <Connector />
        <FlowCard icon="sparkles" tone="green" label="Solution" sub="Shipped fast" />
      </div>

      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {solved.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--neutral-50)", borderRadius: Math.max(8, radius - 6), padding: "10px 12px" }}>
            <span style={{ width: 20, height: 20, borderRadius: 999, flexShrink: 0, background: s.done ? "var(--color-success-surface)" : "var(--red-50)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name={s.done ? "check" : "loader"} size={13} strokeWidth={2.6} color={s.done ? "var(--color-success)" : "var(--brand-red)"} />
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--brand-ink)" }}>{s.label}</span>
            <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, color: s.done ? "var(--color-success)" : "var(--brand-red)", background: s.done ? "var(--color-success-surface)" : "var(--red-50)", padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>{s.tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Animated GPS / fleet dashboard mockup ────────────────────────────────── */
export function FleetDashboard({ radius = 16 }) {
  const reduce = prefersReduced();
  const markerRef = React.useRef(null);
  const pathRef = React.useRef(null);
  const speedRef = React.useRef(null);

  React.useEffect(() => {
    if (reduce) return;
    const path = pathRef.current;
    const marker = markerRef.current;
    if (!path || !marker) return;
    const len = path.getTotalLength();
    let raf;
    let start;
    const dur = 9000;
    const loop = (now) => {
      if (!start) start = now;
      const t = ((now - start) % dur) / dur;
      const pt = path.getPointAtLength(t * len);
      marker.setAttribute("transform", `translate(${pt.x} ${pt.y})`);
      if (speedRef.current) {
        const spd = 38 + Math.round(18 * Math.abs(Math.sin(t * Math.PI * 4)));
        speedRef.current.textContent = spd;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduce]);

  const vehicles = [
    { id: "TH-7821", driver: "Somchai P.", status: "Moving", tone: "var(--color-success)", speed: "52 km/h" },
    { id: "TH-4410", driver: "Anan W.", status: "Idle", tone: "var(--color-warning)", speed: "0 km/h" },
    { id: "TH-9032", driver: "Niran T.", status: "Moving", tone: "var(--color-success)", speed: "44 km/h" },
    { id: "TH-2256", driver: "Kamol S.", status: "Stopped", tone: "var(--neutral-400)", speed: "0 km/h" }
  ];

  return (
    <div style={{ background: "var(--surface-card)", borderRadius: radius + 6, boxShadow: "var(--shadow-lg)", border: "1px solid var(--border-subtle)", overflow: "hidden", fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)", background: "var(--neutral-25)" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => <span key={i} style={{ width: 10, height: 10, borderRadius: 999, background: "var(--neutral-300)" }} />)}
        </div>
        <div style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>rework · live fleet</div>
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "var(--color-success)" }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: "var(--color-success)", animation: reduce ? "none" : "rwPulse 2s infinite" }} />
          LIVE
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", borderBottom: "1px solid var(--border-subtle)" }}>
        {[
          { k: "Active", v: "18", sub: "vehicles" },
          { k: "On route", v: "12", sub: "deliveries" },
          { k: "Avg speed", v: <span ref={speedRef}>46</span>, sub: "km/h" }
        ].map((m, i) => (
          <div key={i} style={{ padding: "14px 16px", borderRight: i < 2 ? "1px solid var(--border-subtle)" : "none" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{m.k}</div>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--brand-ink)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{m.v}<span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-subtle)", marginLeft: 4 }}>{m.sub}</span></div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr" }}>
        <div style={{ position: "relative", background: "var(--neutral-50)", minHeight: 248 }}>
          <svg viewBox="0 0 320 260" width="100%" height="100%" style={{ display: "block" }} preserveAspectRatio="xMidYMid slice">
            <g stroke="var(--neutral-200)" strokeWidth="1">
              {[40, 90, 140, 190, 240].map((y) => <line key={"h" + y} x1="0" y1={y} x2="320" y2={y} />)}
              {[60, 120, 180, 240, 290].map((x) => <line key={"v" + x} x1={x} y1="0" x2={x} y2="260" />)}
            </g>
            <g stroke="var(--neutral-300)" strokeWidth="6" strokeLinecap="round" fill="none">
              <path d="M0 150 H320" />
              <path d="M120 0 V260" />
            </g>
            <path ref={pathRef} d="M28 224 C 70 180, 60 120, 120 110 S 220 130, 236 78 S 280 44, 300 40" fill="none" stroke="var(--brand-red)" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="2 9" opacity="0.9" />
            <circle cx="28" cy="224" r="6" fill="var(--brand-ink)" />
            <g transform="translate(300 40)">
              <circle r="7" fill="var(--brand-red)" />
              <circle r="3" fill="#fff" />
            </g>
            <g transform="translate(96 188)"><circle r="5" fill="var(--neutral-400)" stroke="#fff" strokeWidth="2" /></g>
            <g transform="translate(208 64)"><circle r="5" fill="var(--color-warning)" stroke="#fff" strokeWidth="2" /></g>
            <g ref={markerRef} transform="translate(28 224)">
              <circle r="13" fill="var(--brand-red)" opacity="0.16" />
              <circle r="7" fill="var(--brand-red)" stroke="#fff" strokeWidth="2.5" />
            </g>
          </svg>
          <div style={{ position: "absolute", left: 12, bottom: 12, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(6px)", borderRadius: 8, padding: "7px 10px", boxShadow: "var(--shadow-sm)", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--brand-red)", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--brand-ink)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>TH-7821</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>· Bangkok → Nonthaburi</span>
          </div>
        </div>

        <div style={{ borderLeft: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", minWidth: 0 }}>
          {vehicles.map((v, i) => (
            <div key={v.id} style={{ padding: "11px 12px", borderBottom: i < vehicles.length - 1 ? "1px solid var(--border-subtle)" : "none", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: v.tone, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--brand-ink)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{v.id}</span>
                <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: v.tone, whiteSpace: "nowrap" }}>{v.status}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, paddingLeft: 13, minWidth: 0 }}>
                <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.driver}</span>
                <span style={{ fontSize: 10.5, color: "var(--text-subtle)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 }}>{v.speed}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Inner-page hero schematics ───────────────────────────────────────────── */
function ArtFrame({ label, status, statusTone = "var(--color-success)", radius = 16, children }) {
  return (
    <div style={{ background: "var(--surface-card)", borderRadius: radius + 6, boxShadow: "var(--shadow-lg)", border: "1px solid var(--border-subtle)", overflow: "hidden", fontFamily: "var(--font-sans)", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)", background: "var(--neutral-25)" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => <span key={i} style={{ width: 10, height: 10, borderRadius: 999, background: "var(--neutral-300)" }} />)}
        </div>
        <div style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: "var(--text-muted)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{label}</div>
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: statusTone, whiteSpace: "nowrap" }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: statusTone }} />
          {status}
        </span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ArtServices({ radius }) {
  const layers = [
    { t: "Apps & portals", icon: "layout-dashboard", red: true },
    { t: "Platform & APIs", icon: "server", red: false },
    { t: "Telematics layer", icon: "radio-tower", red: false },
    { t: "Devices & sensors", icon: "cpu", red: false }
  ];
  return (
    <ArtFrame label="architecture" status="4 layers" radius={radius}>
      <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12, position: "relative" }}>
        <div style={{ position: "absolute", left: 33, top: 30, bottom: 30, width: 2, background: "var(--neutral-200)" }} />
        {layers.map((l, i) => (
          <div key={i} style={{ position: "relative", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ position: "relative", zIndex: 1, width: 24, height: 24, borderRadius: 999, flexShrink: 0, background: l.red ? "var(--brand-red)" : "var(--surface-card)", border: l.red ? "none" : "2px solid var(--neutral-300)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              {l.red && <span style={{ width: 8, height: 8, borderRadius: 999, background: "#fff" }} />}
            </span>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, background: l.red ? "var(--red-50)" : "var(--neutral-50)", borderRadius: Math.max(8, radius - 4), padding: "12px 14px", border: l.red ? "1px solid var(--border-brand)" : "1px solid var(--border-subtle)" }}>
              <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: l.red ? "var(--brand-red)" : "var(--neutral-100)", color: l.red ? "#fff" : "var(--brand-ink)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name={l.icon} size={17} strokeWidth={1.9} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--brand-ink)" }}>{l.t}</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                {[14, 22, 10].map((w, j) => <span key={j} style={{ width: w, height: 5, borderRadius: 3, background: l.red ? "var(--red-200)" : "var(--neutral-200)" }} />)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </ArtFrame>
  );
}

function ArtProducts({ radius }) {
  const reduce = prefersReduced();
  const cx = 224;
  const cy = 132;
  const nodes = [
    { x: 58, y: 56, label: "IoT" },
    { x: 52, y: 196, label: "GPS" },
    { x: 396, y: 52, label: "API" },
    { x: 402, y: 200, label: "DATA" }
  ];
  return (
    <ArtFrame label="ai · digital" status="processing" statusTone="var(--brand-red)" radius={radius}>
      <div style={{ position: "relative", height: 284, background: "var(--neutral-50)" }}>
        <svg viewBox="0 0 448 264" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
          <g style={{ stroke: "var(--neutral-200)" }} strokeWidth="1">
            {[40, 92, 144, 196].map((y) => <line key={"h" + y} x1="0" y1={y} x2="448" y2={y} />)}
            {[64, 144, 224, 304, 384].map((x) => <line key={"v" + x} x1={x} y1="0" x2={x} y2="264" />)}
          </g>
          <g style={{ stroke: "var(--neutral-300)" }} strokeWidth="1.5">
            {nodes.map((n, i) => <line key={i} x1={n.x} y1={n.y} x2={cx} y2={cy} />)}
          </g>
          {!reduce && [0, 1].map((i) => (
            <circle key={i} cx={cx} cy={cy} r="40" fill="none" style={{ stroke: "var(--brand-red)" }} strokeWidth="1.5">
              <animate attributeName="r" values="40;82" dur="2.8s" begin={`${i * 1.4}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0" dur="2.8s" begin={`${i * 1.4}s`} repeatCount="indefinite" />
            </circle>
          ))}
          {!reduce && nodes.map((n, i) => (
            <circle key={i} r="3.5" style={{ fill: "var(--brand-red)" }}>
              <animate attributeName="cx" values={`${n.x};${cx}`} dur="1.8s" begin={`${i * 0.45}s`} repeatCount="indefinite" />
              <animate attributeName="cy" values={`${n.y};${cy}`} dur="1.8s" begin={`${i * 0.45}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.85;1" dur="1.8s" begin={`${i * 0.45}s`} repeatCount="indefinite" />
            </circle>
          ))}
          {nodes.map((n, i) => (
            <g key={i} transform={`translate(${n.x} ${n.y})`}>
              <circle r="20" style={{ fill: "var(--surface-card)" }} stroke="var(--neutral-300)" strokeWidth="2" />
              <text textAnchor="middle" dy="3.5" style={{ fill: "var(--text-muted)", fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 10 }}>{n.label}</text>
            </g>
          ))}
          <g transform={`translate(${cx} ${cy})`}>
            <rect x="-40" y="-40" width="80" height="80" rx="20" style={{ fill: "var(--brand-red)" }} />
            <g style={{ stroke: "var(--brand-red)" }} strokeWidth="3" strokeLinecap="round">
              {[-20, 0, 20].map((o) => <line key={"t" + o} x1={o} y1="-40" x2={o} y2="-48" />)}
              {[-20, 0, 20].map((o) => <line key={"b" + o} x1={o} y1="40" x2={o} y2="48" />)}
              {[-20, 0, 20].map((o) => <line key={"l" + o} x1="-40" y1={o} x2="-48" y2={o} />)}
              {[-20, 0, 20].map((o) => <line key={"r" + o} x1="40" y1={o} x2="48" y2={o} />)}
            </g>
            <path d="M0 -22 L2.4 -10 L14 -7.5 L2.4 -5 L0 7 L-2.4 -5 L-14 -7.5 L-2.4 -10 Z" fill="#fff" opacity="0.95" />
            <text textAnchor="middle" dy="20" style={{ fill: "#fff", fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 20, letterSpacing: "0.04em" }}>AI</text>
          </g>
        </svg>
        <div style={{ position: "absolute", left: 14, bottom: 14, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(6px)", borderRadius: 8, padding: "8px 12px", boxShadow: "var(--shadow-sm)", display: "flex", alignItems: "center", gap: 9 }}>
          <Icon name="sparkles" size={15} color="var(--brand-red)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--brand-ink)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>AI insights · live</span>
        </div>
      </div>
    </ArtFrame>
  );
}

function ArtArticles({ radius }) {
  const reduce = prefersReduced();
  const line = "M36 150 L104 126 L172 138 L240 96 L308 110 L384 60";
  const feed = [
    { tag: "AI", y: 196 },
    { tag: "CLOUD", y: 226 },
    { tag: "IOT", y: 256 }
  ];
  return (
    <ArtFrame label="tech · trends" status="live" statusTone="var(--brand-red)" radius={radius}>
      <div style={{ position: "relative", height: 284, background: "var(--neutral-50)" }}>
        <svg viewBox="0 0 448 284" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
          <g style={{ stroke: "var(--neutral-200)" }} strokeWidth="1">
            {[56, 96, 136, 176].map((y) => <line key={y} x1="28" y1={y} x2="420" y2={y} />)}
          </g>
          <path d={line + " L384 176 L36 176 Z"} style={{ fill: "var(--brand-red)" }} opacity="0.08" />
          <path d={line} fill="none" style={{ stroke: "var(--brand-red)" }} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          {[[36, 150], [104, 126], [172, 138], [240, 96], [308, 110]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3.5" style={{ fill: "var(--surface-card)" }} stroke="var(--brand-ink)" strokeWidth="1.5" />
          ))}
          <circle cx="384" cy="60" r="14" style={{ fill: "var(--brand-red)" }} opacity="0.16">
            {!reduce && <animate attributeName="r" values="9;17;9" dur="2.4s" repeatCount="indefinite" />}
          </circle>
          <circle cx="384" cy="60" r="5.5" style={{ fill: "var(--brand-red)" }} stroke="#fff" strokeWidth="2" />
          {!reduce && (
            <circle r="4" style={{ fill: "var(--brand-ink)" }}>
              <animateMotion dur="3s" repeatCount="indefinite" path={line} />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="3s" repeatCount="indefinite" />
            </circle>
          )}
          <g transform="translate(360 32)">
            <path d="M0 0 L6 -8 L12 0 Z" style={{ fill: "var(--brand-red)" }} />
            <text x="18" y="0" dy="0" style={{ fill: "var(--brand-red)", fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 13 }}>+24%</text>
          </g>
          {feed.map((r, i) => (
            <g key={i}>
              <rect x="28" y={r.y - 11} width={r.tag.length * 8 + 16} height="20" rx="6" style={{ fill: i === 0 ? "var(--brand-red)" : "var(--neutral-200)" }} />
              <text x={28 + (r.tag.length * 8 + 16) / 2} y={r.y} dy="4" textAnchor="middle" style={{ fill: i === 0 ? "#fff" : "var(--text-muted)", fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 10 }}>{r.tag}</text>
              <rect x={44 + r.tag.length * 8 + 16} y={r.y - 8} width={i === 0 ? 250 : 210 - i * 26} height="7" rx="3.5" style={{ fill: "var(--neutral-300)" }} />
              <rect x={44 + r.tag.length * 8 + 16} y={r.y + 3} width={150 - i * 18} height="5" rx="2.5" style={{ fill: "var(--neutral-200)" }} />
              {i === 0 && !reduce && (
                <circle cx="410" cy={r.y - 2} r="4" style={{ fill: "var(--brand-red)" }}>
                  <animate attributeName="opacity" values="1;0.2;1" dur="1.6s" repeatCount="indefinite" />
                </circle>
              )}
            </g>
          ))}
        </svg>
      </div>
    </ArtFrame>
  );
}

function ArtCareers({ radius }) {
  const nodes = [
    { x: 80, y: 70, r: 22, red: false, label: "SP" },
    { x: 224, y: 48, r: 18, red: true },
    { x: 356, y: 92, r: 22, red: false, label: "AN" },
    { x: 150, y: 170, r: 18, red: false },
    { x: 300, y: 196, r: 22, red: false, label: "NT" },
    { x: 392, y: 188, r: 14, red: false }
  ];
  const edges = [[0, 1], [1, 2], [0, 3], [1, 3], [1, 4], [2, 4], [4, 5], [3, 4]];
  return (
    <ArtFrame label="the team" status="4 roles open" statusTone="var(--brand-red)" radius={radius}>
      <div style={{ position: "relative", height: 284, background: "var(--neutral-50)" }}>
        <svg viewBox="0 0 440 252" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
          <g style={{ stroke: "var(--neutral-300)" }} strokeWidth="1.5">
            {edges.map(([a, b], i) => <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} />)}
          </g>
          {nodes.map((n, i) => (
            <g key={i} transform={`translate(${n.x} ${n.y})`}>
              <circle r={n.r} style={{ fill: n.red ? "var(--brand-red)" : "var(--surface-card)" }} stroke={n.red ? "none" : "var(--neutral-300)"} strokeWidth="2" />
              {n.red ? (
                <g style={{ stroke: "#fff" }} strokeWidth="2" fill="none" strokeLinecap="round">
                  <line x1="0" y1="-6" x2="0" y2="6" />
                  <line x1="-6" y1="0" x2="6" y2="0" />
                </g>
              ) : n.label ? (
                <text textAnchor="middle" dy="4.5" style={{ fill: "var(--brand-ink)", fontFamily: "var(--font-sans)", fontWeight: 700, fontSize: 13 }}>{n.label}</text>
              ) : (
                <circle r={n.r * 0.42} style={{ fill: "var(--neutral-200)" }} />
              )}
            </g>
          ))}
        </svg>
      </div>
    </ArtFrame>
  );
}

function ArtContact({ radius }) {
  const reduce = prefersReduced();
  return (
    <ArtFrame label="get in touch" status="~1 business day" radius={radius}>
      <div style={{ position: "relative", height: 284, background: "var(--neutral-50)" }}>
        <svg viewBox="0 0 440 284" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ display: "block" }}>
          <path d="M70 232 C 150 232, 150 120, 250 110 S 360 96, 384 72" fill="none" style={{ stroke: "var(--brand-red)" }} strokeWidth="3" strokeLinecap="round" strokeDasharray="2 9" />
          <circle cx="70" cy="232" r="6" style={{ fill: "var(--brand-ink)" }} />
          <g transform="translate(384 64)">
            <circle r="15" style={{ fill: "var(--brand-red)" }} opacity="0.16">
              {!reduce && <animate attributeName="r" values="10;18;10" dur="2.4s" repeatCount="indefinite" />}
            </circle>
            <path d="M0 -14 C 8 -14, 13 -9, 13 -1 C 13 8, 0 18, 0 18 C 0 18, -13 8, -13 -1 C -13 -9, -8 -14, 0 -14 Z" style={{ fill: "var(--brand-red)" }} stroke="#fff" strokeWidth="2" />
            <circle cy="-1" r="4.5" fill="#fff" />
          </g>
        </svg>
        <div style={{ position: "absolute", left: 22, bottom: 24, width: 188, background: "var(--surface-card)", borderRadius: Math.max(10, radius - 2), border: "1px solid var(--border-subtle)", boxShadow: "var(--shadow-md)", padding: 14, display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: "var(--brand-red)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="mail" size={15} color="#fff" />
            </span>
            <span style={{ width: 96, height: 7, borderRadius: 4, background: "var(--neutral-200)" }} />
          </div>
          {["100%", "86%", "64%"].map((w, i) => <span key={i} style={{ width: w, height: 6, borderRadius: 3, background: "var(--neutral-200)" }} />)}
        </div>
      </div>
    </ArtFrame>
  );
}

export function HeroArt({ kind, radius = 16 }) {
  const map = { services: ArtServices, products: ArtProducts, articles: ArtArticles, careers: ArtCareers, contact: ArtContact };
  const C = map[kind];
  return C ? <C radius={radius} /> : null;
}

/* ── Inner-page hero with drifting particles ──────────────────────────────── */
function heroParticles() {
  let s = 20260615;
  const rnd = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  const layers = [
    { blur: 4, op: 0.2, min: 3, max: 6 },
    { blur: 2, op: 0.2, min: 5, max: 9 },
    { blur: 0, op: 0.2, min: 7, max: 13 }
  ];
  const floats = ["rwFloatA", "rwFloatB", "rwFloatC"];
  const arr = [];
  for (let i = 0; i < 170; i++) {
    const layer = layers[i % 3];
    arr.push({
      left: rnd() * 100,
      top: rnd() * 100,
      size: layer.min + rnd() * (layer.max - layer.min),
      blur: layer.blur,
      op: layer.op,
      float: floats[i % 3],
      dur: 5.3 + rnd() * 5.3,
      delay: -rnd() * 12
    });
  }
  return arr;
}

function HeroParticles() {
  const parts = React.useMemo(heroParticles, []);
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>
      {parts.map((p, i) => (
        <span
          key={i}
          className={"rw-particle " + p.float}
          style={{
            position: "absolute",
            left: p.left + "%",
            top: p.top + "%",
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: "var(--neutral-400)",
            opacity: p.op,
            filter: p.blur ? `blur(${p.blur}px)` : "none",
            animationDuration: p.dur + "s",
            animationDelay: p.delay + "s",
            willChange: "transform"
          }}
        />
      ))}
    </div>
  );
}

export function PageHero({ eyebrow, title, lede, art, radius = 16, fontFamily = "var(--font-display)" }) {
  return (
    <section style={{ background: "var(--surface-page)", borderBottom: "1px solid var(--border-subtle)", position: "relative", overflow: "hidden" }}>
      <HeroParticles />
      <div aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: "var(--brand-red)", zIndex: 1 }} />
      <Container style={{ position: "relative", zIndex: 1 }}>
        <div
          className={art ? "rw-pagehero" : ""}
          style={{
            display: art ? "grid" : "block",
            gridTemplateColumns: art ? "1.05fr 0.95fr" : undefined,
            gap: "var(--space-9)",
            alignItems: "center",
            padding: "clamp(2.5rem, 6vw, var(--space-10)) 0 clamp(2rem, 4vw, var(--space-8))"
          }}
        >
          <div style={{ maxWidth: art ? 540 : 720 }}>
            <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
            <Reveal delay={70}>
              <h1 style={{ margin: "16px 0 0", fontFamily, fontWeight: 800, fontSize: "clamp(2.2rem, 5vw, var(--text-4xl))", lineHeight: 1.04, letterSpacing: "var(--tracking-tighter)", color: "var(--brand-ink)", textWrap: "balance" }}>{title}</h1>
            </Reveal>
            {lede && <Reveal delay={140}><p style={{ margin: "18px 0 0", fontSize: "var(--text-md)", lineHeight: 1.65, color: "var(--text-body)", maxWidth: 560, textWrap: "pretty" }}>{lede}</p></Reveal>}
          </div>
          {art && <Reveal delay={140} y={26} className="rw-pagehero-art">{art}</Reveal>}
        </div>
      </Container>
    </section>
  );
}

/* ── Partner logos (inline-SVG brand lockups) ─────────────────────────────── */
export function PartnerLogo({ name }) {
  const wordStyle = { fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em", color: "var(--brand-ink)", lineHeight: 1, whiteSpace: "nowrap" };
  const wrap = { display: "inline-flex", alignItems: "center", gap: 10 };

  if (name === "Framer") {
    return (
      <span style={wrap}>
        <svg width="20" height="30" viewBox="0 0 14 21" aria-hidden="true">
          <path d="M0 0h14v7H7z" fill="#0099FF" />
          <path d="M0 7h7l7 7H7v7L0 14z" fill="#0099FF" />
        </svg>
        <span style={wordStyle}>Framer</span>
      </span>
    );
  }
  if (name === "Verpex") {
    return (
      <span style={wrap}>
        <svg width="26" height="28" viewBox="0 0 26 28" aria-hidden="true">
          <path d="M13 1.5l10 5.75v11.5L13 26.5 3 20.75V7.25z" fill="none" stroke="#16A75C" strokeWidth="2" strokeLinejoin="round" />
          <path d="M8.5 10l4.5 8 4.5-8" fill="none" stroke="#16A75C" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={wordStyle}>Verpex</span>
      </span>
    );
  }
  if (name === "GoDaddy") {
    return (
      <span style={wrap}>
        <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="16" r="15" fill="#1BDBDB" />
          <path d="M21 11.2a7 7 0 1 0 1.4 6.3h-6.1v-2.6H25c.1.6.2 1.3.2 2 0 5.4-3.9 9.3-9.2 9.3A9.2 9.2 0 1 1 22.7 9z" fill="#0A1F2B" />
        </svg>
        <span style={wordStyle}>GoDaddy</span>
      </span>
    );
  }
  return <span style={wordStyle}>{name}</span>;
}
